<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useShopifyStore } from '@/stores/shopify';
import { useGlobalStore } from '@/stores/global';
import Loading from '@components/icons/Loading.vue';

const shopify = useShopifyStore();
const global = useGlobalStore();
const announcementBar = ref(null);
let observer = null;

const hasAnnouncement = computed(() => Boolean(shopify.shop.announcement_bar_text));
const showBar = computed(() => !shopify.shopLoaded || hasAnnouncement.value);

function observeBar() {
	if (!observer || !announcementBar.value) return;
	observer.disconnect();
	observer.observe(announcementBar.value);
}

watch(showBar, async (visible) => {
	document.documentElement.style.setProperty(
		'--announcement-bar-height',
		visible ? '40px' : '0px'
	);

	if (!visible) {
		global.setAnnouncementBarVisible(false);
		return;
	}

	await nextTick();
	observeBar();
}, { immediate: true });

onMounted(() => {
	observer = new IntersectionObserver(
		(entries) => {
			global.setAnnouncementBarVisible(entries[0].isIntersecting);
		},
		{ threshold: 0 }
	);
	observeBar();
});

onUnmounted(() => {
	if (observer) {
		observer.disconnect();
	}
});
</script>

<template>
  <div
    v-if="showBar"
    ref="announcementBar"
    class="bg-bg-inverted text-text-inverted flex items-center justify-center p-8 h-(--announcement-bar-height)"
  >
    <div v-if="hasAnnouncement" class="flex items-center gap-8">
        <img v-if="shopify.shop.announcement_bar_icon" :src="shopify.shop.announcement_bar_icon" :alt="shopify.shop.announcement_bar_icon_alt || ''" />
        <p class="body-sm">{{ shopify.shop.announcement_bar_text }}</p>
    </div>
    <Loading v-else class="size-12 animate-spin text-white" />
  </div>
</template>
